import React, { useState, useEffect, useCallback } from 'react';
import { Plus, BookOpen, ChevronDown, CheckCircle, XCircle } from 'lucide-react';

export default function ProgramaCapacitaciones({ onBack }) {
  const [vista, setVista] = useState('cronograma'); // 'cronograma', 'ejecucion', 'indicadores'
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ text: '', type: '' });

  const [nuevaActividad, setNuevaActividad] = useState({
    fase_phva: 'PLANEAR',
    actividad: '',
    objetivo: '',
    intensidad: '',
    modalidad: 'Presencial',
    dirigido_a: '',
    numero_convocados: '',
    responsable: '',
    recursos: '',
    mes_programado: 'ENE'
  });

  const cargarPrograma = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;
      const res = await fetch(`${API_URL}/api/capacitaciones/programa`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) }
      });
      const data = await res.json();
      if (data.success) setActividades(data.data || []);
    } catch (e) {
      console.error("Error al cargar programa:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarPrograma();
  }, [cargarPrograma]);

  const handleCrear = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;
      const res = await fetch(`${API_URL}/api/capacitaciones/programa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(nuevaActividad)
      });
      const data = await res.json();
      if (data.success) {
        setMensaje({ text: '¡Actividad programada con éxito en el cronograma!', type: 'success' });
        setNuevaActividad({
          fase_phva: 'PLANEAR',
          actividad: '',
          objetivo: '',
          intensidad: '',
          modalidad: 'Presencial',
          dirigido_a: '',
          numero_convocados: '',
          responsable: '',
          recursos: '',
          mes_programado: 'ENE'
        });
        cargarPrograma();
      } else {
        setMensaje({ text: data.message || 'Error al guardar', type: 'error' });
      }
    } catch (err) {
      setMensaje({ text: 'Error de red', type: 'error' });
    }
  };

  const handleActualizarEstado = async (id, nuevoEstado) => {
    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;
      const res = await fetch(`${API_URL}/api/capacitaciones/estado/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ estado: nuevoEstado })
      });
      const data = await res.json();
      if (data.success) {
        setMensaje({ text: `¡Actividad marcada como ${nuevoEstado === 'EJECUTADO' ? 'Ejecutada' : 'No Ejecutada'}!`, type: 'success' });
        cargarPrograma();
      } else {
        setMensaje({ text: data.message || 'Error al actualizar', type: 'error' });
      }
    } catch (err) {
      setMensaje({ text: 'Error de red al actualizar el estado', type: 'error' });
    }
  };

  // Cálculos de Indicadores
  const totalProgramadas = actividades.length;
  const totalEjecutadas = actividades.filter(a => a.estado === 'EJECUTADO').length;
  const totalNoEjecutadas = actividades.filter(a => a.estado === 'NO_EJECUTADO').length;
  const porcentajeCumplimiento = totalProgramadas > 0 ? ((totalEjecutadas / totalProgramadas) * 100).toFixed(1) : 0;

  const inputClases = "w-full px-4 !m-0 h-[48px] bg-white/60 backdrop-blur-md border border-white/60 rounded-xl text-sm font-bold text-slate-800 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-inner transition-all";
  const selectClases = "w-full pl-4 pr-10 !m-0 h-[48px] bg-white/60 backdrop-blur-md border border-white/60 rounded-xl text-sm font-bold text-slate-800 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer shadow-inner transition-all";

  // Clases unificadas para garantizar que los botones midan exactamente lo mismo
  const btnEjecutado = "h-10 min-w-[150px] px-4 bg-emerald-500/20 hover:bg-emerald-500/30 backdrop-blur-md border border-emerald-500/40 text-emerald-900 rounded-xl text-[11px] font-black uppercase shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2 active:scale-95 whitespace-nowrap";
  const btnNoEjecutado = "h-10 min-w-[150px] px-4 bg-rose-500/20 hover:bg-rose-500/30 backdrop-blur-md border border-rose-500/40 text-rose-900 rounded-xl text-[11px] font-black uppercase shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2 active:scale-95 whitespace-nowrap";
  const labelEjecutado = "h-10 min-w-[150px] px-4 bg-emerald-500/20 backdrop-blur-md border border-emerald-500/40 text-emerald-900 rounded-xl text-[11px] font-black uppercase inline-flex items-center justify-center gap-2 shadow-sm whitespace-nowrap";
  const labelNoEjecutado = "h-10 min-w-[150px] px-4 bg-rose-500/20 backdrop-blur-md border border-rose-500/40 text-rose-900 rounded-xl text-[11px] font-black uppercase inline-flex items-center justify-center gap-2 shadow-sm whitespace-nowrap";

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-6 animate-fade-in">
      <div className="bg-white/20 backdrop-blur-3xl border border-white/40 shadow-2xl rounded-[34px] p-6 sm:p-10 relative flex flex-col overflow-hidden">
        
        {/* Cabecera */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 border-b border-white/30 pb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl border border-blue-200 shadow-sm">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              Programa de Capacitación Continua (PG-CD-01)
            </h2>
            <p className="text-sm font-bold text-slate-600 mt-2">Metodología PHVA: Cronograma, planeación metodológica, ejecución y cumplimiento</p>
          </div>
          
          <button 
            onClick={onBack}
            className="flex items-center gap-2 bg-white/60 hover:bg-white/80 text-slate-800 px-4 py-2.5 rounded-xl backdrop-blur-md border border-white/60 shadow-sm transition-all font-black text-xs cursor-pointer"
          >
            Volver al Menú
          </button>
        </div>

        {/* Pestañas de Navegación */}
        <div className="flex bg-white/40 backdrop-blur-md p-1.5 rounded-2xl border border-white/60 max-w-xl mx-auto mb-6 shadow-inner w-full">
          <button 
            onClick={() => setVista('cronograma')}
            className={`flex-1 py-2 text-xs font-black rounded-xl transition-all cursor-pointer ${vista === 'cronograma' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            📅 1. Planeación y Cronograma
          </button>
          <button 
            onClick={() => setVista('ejecucion')}
            className={`flex-1 py-2 text-xs font-black rounded-xl transition-all cursor-pointer ${vista === 'ejecucion' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            ✅ 2. Control y Seguimiento
          </button>
          <button 
            onClick={() => setVista('indicadores')}
            className={`flex-1 py-2 text-xs font-black rounded-xl transition-all cursor-pointer ${vista === 'indicadores' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            📊 3. Indicadores (%)
          </button>
        </div>

        {mensaje.text && (
          <div className={`mb-6 p-4 rounded-xl font-bold text-xs shadow-sm ${mensaje.type === 'success' ? 'bg-emerald-100/80 backdrop-blur-md border border-emerald-300 text-emerald-800' : 'bg-rose-100/80 backdrop-blur-md border border-rose-300 text-rose-800'}`}>
            {mensaje.text}
          </div>
        )}

        {/* VISTA 1: CRONOGRAMA */}
        {vista === 'cronograma' && (
          <div className="space-y-6 animate-fade-in">
            <form onSubmit={handleCrear} className="bg-white/40 backdrop-blur-md p-6 rounded-3xl border border-white/60 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-5 items-end">
              
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Fase Ciclo PHVA</label>
                <div className="relative flex items-center">
                  <select value={nuevaActividad.fase_phva} onChange={e => setNuevaActividad({...nuevaActividad, fase_phva: e.target.value})} className={selectClases}>
                    <option value="PLANEAR">PLANEAR</option>
                    <option value="HACER">HACER</option>
                    <option value="VERIFICAR">VERIFICAR</option>
                    <option value="ACTUAR">ACTUAR</option>
                  </select>
                  <ChevronDown className="w-5 h-5 text-slate-400 absolute right-3 pointer-events-none" />
                </div>
              </div>

              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Tema o Actividad</label>
                <input type="text" value={nuevaActividad.actividad} onChange={e => setNuevaActividad({...nuevaActividad, actividad: e.target.value})} required className={inputClases} />
              </div>

              <div className="flex flex-col gap-1.5 sm:col-span-3">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Objetivo de la Capacitación</label>
                <input type="text" value={nuevaActividad.objetivo} onChange={e => setNuevaActividad({...nuevaActividad, objetivo: e.target.value})} required className={inputClases} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Modalidad</label>
                <div className="relative flex items-center">
                  <select value={nuevaActividad.modalidad} onChange={e => setNuevaActividad({...nuevaActividad, modalidad: e.target.value})} className={selectClases}>
                    <option value="Presencial">Presencial</option>
                    <option value="Virtual">Virtual</option>
                    <option value="Mixta">Mixta</option>
                  </select>
                  <ChevronDown className="w-5 h-5 text-slate-400 absolute right-3 pointer-events-none" />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Intensidad Horaria</label>
                <input type="text" value={nuevaActividad.intensidad} onChange={e => setNuevaActividad({...nuevaActividad, intensidad: e.target.value})} required className={inputClases} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Mes Programado</label>
                <div className="relative flex items-center">
                  <select value={nuevaActividad.mes_programado} onChange={e => setNuevaActividad({...nuevaActividad, mes_programado: e.target.value})} className={selectClases}>
                    {['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <ChevronDown className="w-5 h-5 text-slate-400 absolute right-3 pointer-events-none" />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Dirigido A</label>
                <input type="text" value={nuevaActividad.dirigido_a} onChange={e => setNuevaActividad({...nuevaActividad, dirigido_a: e.target.value})} required className={inputClases} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Número de Convocados (Empleados)</label>
                <input type="number" value={nuevaActividad.numero_convocados} onChange={e => setNuevaActividad({...nuevaActividad, numero_convocados: e.target.value})} required className={inputClases} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Responsable</label>
                <input type="text" value={nuevaActividad.responsable} onChange={e => setNuevaActividad({...nuevaActividad, responsable: e.target.value})} required className={inputClases} />
              </div>

              <div className="flex flex-col gap-1.5 sm:col-span-3">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Recursos Necesarios</label>
                <input type="text" value={nuevaActividad.recursos} onChange={e => setNuevaActividad({...nuevaActividad, recursos: e.target.value})} required className={inputClases} />
              </div>

              <div className="sm:col-span-3 flex justify-end pt-2">
                <button type="submit" className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg cursor-pointer">
                  <Plus className="w-5 h-5" /> Programar Actividad en el Cronograma
                </button>
              </div>
            </form>

            <div className="bg-white/40 backdrop-blur-md rounded-3xl border border-white/60 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs min-w-[900px]">
                  <thead className="bg-white/60 text-slate-700 font-black uppercase text-[10px]">
                    <tr>
                      <th className="p-4">Fase PHVA</th>
                      <th className="p-4">Actividad y Objetivo</th>
                      <th className="p-4">Modalidad / Horas</th>
                      <th className="p-4">Dirigido A / Convocados</th>
                      <th className="p-4">Mes</th>
                      <th className="p-4">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/40 font-medium text-slate-800">
                    {actividades.map((a) => (
                      <tr key={a.id} className="hover:bg-white/40">
                        <td className="p-4 font-bold">
                          <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black ${a.fase_phva === 'PLANEAR' ? 'bg-blue-100 text-blue-800' : a.fase_phva === 'HACER' ? 'bg-emerald-100 text-emerald-800' : 'bg-purple-100 text-purple-800'}`}>
                            {a.fase_phva}
                          </span>
                        </td>
                        <td className="p-4">
                          <p className="font-bold text-slate-900">{a.actividad}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">{a.objetivo}</p>
                        </td>
                        <td className="p-4 font-semibold text-slate-700">
                          {a.modalidad} ({a.intensidad})
                        </td>
                        <td className="p-4">
                          <p className="font-bold text-slate-700">{a.dirigido_a}</p>
                          <p className="text-[10px] text-blue-600 font-black">Convocados: {a.numero_convocados || 0}</p>
                        </td>
                        <td className="p-4 font-mono font-black text-blue-600">{a.mes_programado}</td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black ${a.estado === 'EJECUTADO' ? 'bg-emerald-100 text-emerald-800' : a.estado === 'NO_EJECUTADO' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>
                            {a.estado === 'EJECUTADO' ? 'Ejecutado' : a.estado === 'NO_EJECUTADO' ? 'No Ejecutado' : 'Programado'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {actividades.length === 0 && !loading && (
                <p className="text-center py-10 text-xs font-bold text-slate-500">No hay actividades programadas en el cronograma todavía.</p>
              )}
            </div>
          </div>
        )}

        {/* VISTA 2: CONTROL Y SEGUIMIENTO CON BOTONES DEL MISMO TAMAÑO */}
        {vista === 'ejecucion' && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white/40 backdrop-blur-md p-6 rounded-3xl border border-white/60 shadow-sm">
              <h3 className="text-emerald-700 font-black text-sm mb-4 uppercase tracking-wider flex items-center gap-2">
                <CheckCircle className="w-5 h-5" /> Control y Seguimiento de Capacitaciones
              </h3>
              <p className="text-xs text-slate-600 mb-6 font-medium">
                Valida cada actividad del cronograma. Al definir su estado, el sistema registrará la validación y ocultará los botones de selección.
              </p>

              <div className="space-y-4">
                {actividades.map((a) => (
                  <div key={a.id} className="bg-white/60 backdrop-blur-md p-4 rounded-2xl border border-white/80 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded text-[9px] font-black">{a.fase_phva}</span>
                        <span className="text-xs font-mono font-bold text-blue-600">Mes: {a.mes_programado}</span>
                      </div>
                      <h4 className="text-sm font-black text-slate-900">{a.actividad}</h4>
                      <p className="text-xs text-slate-600 mt-0.5">Dirigido a: <strong>{a.dirigido_a}</strong> ({a.numero_convocados || 0} convocados)</p>
                    </div>

                    <div className="flex items-center gap-3">
                      {a.estado === 'EJECUTADO' ? (
                        <div className={labelEjecutado}>
                          <CheckCircle className="w-4 h-4 text-emerald-700" /> Ejecutado
                        </div>
                      ) : a.estado === 'NO_EJECUTADO' ? (
                        <div className={labelNoEjecutado}>
                          <XCircle className="w-4 h-4 text-rose-700" /> No Ejecutado
                        </div>
                      ) : (
                        <>
                          <button 
                            onClick={() => handleActualizarEstado(a.id, 'EJECUTADO')}
                            className={btnEjecutado}
                          >
                            ✓ Ejecutado
                          </button>
                          <button 
                            onClick={() => handleActualizarEstado(a.id, 'NO_EJECUTADO')}
                            className={btnNoEjecutado}
                          >
                            ✕ No Ejecutado
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {actividades.length === 0 && (
                  <p className="text-center py-10 text-xs font-bold text-slate-500">Primero programa actividades en el Cronograma Anual.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* VISTA 3: INDICADORES */}
        {vista === 'indicadores' && (
          <div className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
              <div className="bg-white/50 backdrop-blur-md p-6 rounded-3xl border border-white/60 shadow-sm text-center">
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Total Programadas</p>
                <p className="text-3xl font-black text-slate-800 mt-2">{totalProgramadas}</p>
              </div>
              <div className="bg-emerald-50/50 backdrop-blur-md p-6 rounded-3xl border border-emerald-200 shadow-sm text-center">
                <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Ejecutadas</p>
                <p className="text-3xl font-black text-emerald-800 mt-2">{totalEjecutadas}</p>
              </div>
              <div className="bg-rose-50/50 backdrop-blur-md p-6 rounded-3xl border border-rose-200 shadow-sm text-center">
                <p className="text-[10px] font-black text-rose-700 uppercase tracking-widest">No Ejecutadas</p>
                <p className="text-3xl font-black text-rose-800 mt-2">{totalNoEjecutadas}</p>
              </div>
              <div className="bg-orange-50/50 backdrop-blur-md p-6 rounded-3xl border border-orange-200 shadow-sm text-center">
                <p className="text-[10px] font-black text-orange-700 uppercase tracking-widest">% Cumplimiento</p>
                <p className="text-3xl font-black text-orange-800 mt-2">{porcentajeCumplimiento}%</p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}